import { combineReducers, Dispatch, legacy_createStore, Store } from 'redux'
import { shallowEqual, TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { devToolsEnhancer } from '@redux-devtools/extension'
import { EmailState } from '../../shared/ui'
import { emailReducer, selectors as emailSelectors } from './email/reducers'
import * as emailActions from './email/actions'
import * as managementActions from './management/actions'
import * as filesActions from './files/actions'
import { managementReducer, selectors as managementSelectors } from './management/reducer'
import { ManagementState } from './management/type'
import { filesReducer, FilesState, selectors as filesSelectors } from './files/reducer'
import {
  EmailActionTypes,
  LOAD_CONTACT_FIELDS,
  LOAD_EMAIL,
  UPDATE_ENTITY_WITHOUT_HISTORY,
  UPDATE_HASH,
} from './email/actionTypes'
import { ManagementActionTypes } from './management/actionTypes'
import { FilesActions } from './files/actions'
import undoable, { excludeAction, StateWithHistory } from 'redux-undo'

export interface RootState {
  readonly email: StateWithHistory<EmailState>
  readonly management: ManagementState
  readonly files: FilesState
}

const rootReducer = combineReducers({
  email: undoable(emailReducer, {
    filter: excludeAction([
      LOAD_EMAIL,
      LOAD_CONTACT_FIELDS,
      UPDATE_ENTITY_WITHOUT_HISTORY,
      UPDATE_HASH,
    ]),
    syncFilter: true,
  }),
  management: managementReducer,
  files: filesReducer,
})

export function configureStore(): Store<RootState> {
  return process.env.NODE_ENV !== 'production'
    ? (legacy_createStore(rootReducer, {}, devToolsEnhancer()) as Store<RootState>)
    : (legacy_createStore(rootReducer) as Store<RootState>)
}

export type AppDispatch = Dispatch<EmailActionTypes | ManagementActionTypes | FilesActions>

export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector
export const useAppDispatch = () => useDispatch<AppDispatch>()

export function useEmail<T>(selector: (emailState: EmailState) => T): T {
  return useTypedSelector(state => selector(state.email.present), shallowEqual)
}

export function useManagement<T>(selector: (emailState: ManagementState) => T): T {
  return useTypedSelector(state => selector(state.management), shallowEqual)
}

export function useFiles<T>(selector: (emailState: FilesState) => T): T {
  return useTypedSelector(state => selector(state.files), shallowEqual)
}

export {
  emailSelectors,
  managementSelectors,
  emailActions,
  managementActions,
  filesSelectors,
  filesActions,
}
